import { useState, useEffect } from 'react';
import { supabase } from '../supabase';
import type { Database } from '../database.types';
import type { CustomerContact } from '../../types';
import { containsCreditCardNumber } from '../validation';
import OpenAI from 'openai';

let openai: OpenAI | null = null;
try {
  openai = new OpenAI({
    apiKey: import.meta.env.VITE_OPENAI_API_KEY || '',
    dangerouslyAllowBrowser: true
  });
} catch (err) {
  console.error('Failed to initialize OpenAI client:', err);
}

export function useCustomers() {
  const [customers, setCustomers] = useState<Database['public']['Tables']['customers']['Row'][]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [duplicateCustomer, setDuplicateCustomer] = useState<Database['public']['Tables']['customers']['Row'] | null>(null);

  useEffect(() => {
    async function fetchCustomers() {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          setError('User not authenticated');
          return;
        }

        const { data, error } = await supabase
          .from('customers')
          .select('*')
          .eq('company_id', user.id)
          .order('created_at', { ascending: false });

        if (error) throw error;
        setCustomers(data || []);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch customers');
      } finally {
        setLoading(false);
      }
    }

    fetchCustomers();
  }, []);

  const addCustomer = async (customerData: Omit<Database['public']['Tables']['customers']['Insert'], 'company_id' | 'id'>) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Validate required fields
      if (!customerData.first_name?.trim()) {
        throw new Error('First name is required');
      }
      if (!customerData.last_name?.trim()) {
        throw new Error('Last name is required');
      }
      if (!customerData.phone?.trim()) {
        throw new Error('Phone number is required');
      }

      // Clean phone number
      const cleanPhone = customerData.phone.replace(/\D/g, '');
      if (cleanPhone.length !== 10) {
        throw new Error('Please enter a valid 10-digit phone number');
      }

      // Format phone number
      const formattedPhone = `(${cleanPhone.slice(0,3)}) ${cleanPhone.slice(3,6)}-${cleanPhone.slice(6)}`;

      // Check for duplicate phone number
      const { data: existingCustomers } = await supabase
        .from('customers')
        .select('*')
        .eq('company_id', user.id)
        .eq('phone', formattedPhone);

      if (existingCustomers && existingCustomers.length > 0) {
        setDuplicateCustomer(existingCustomers[0]);
        return { duplicate: true, customer: existingCustomers[0] };
      }

      // Create customer
      const { data: customer, error: customerError } = await supabase
        .from('customers')
        .insert([{ 
          ...customerData,
          phone: formattedPhone,
          company_id: user.id,
          // Ensure required fields have values
          address: customerData.address || 'Not provided',
          city: customerData.city || 'Not provided',
          state: customerData.state || 'NA',
          zip_code: customerData.zip_code || '00000'
        }])
        .select()
        .single();

      if (customerError) throw customerError;

      if (!customer) {
        throw new Error('Failed to create customer record');
      }

      // Create contact records
      const promises = [];

      // Create address if provided
      if (customerData.address && customerData.city && customerData.state && customerData.zip_code) {
        promises.push(
          supabase
            .from('customer_addresses')
            .insert([{
              customer_id: customer.id,
              address: customerData.address,
              city: customerData.city,
              state: customerData.state,
              zip_code: customerData.zip_code,
              is_primary: true,
              label: 'Main Address'
            }])
        );
      }

      // Create email if provided
      if (customerData.email) {
        promises.push(
          supabase
            .from('customer_emails')
            .insert([{
              customer_id: customer.id,
              email: customerData.email,
              is_primary: true,
              label: 'Main Email'
            }])
        );
      }

      // Create phone
      promises.push(
        supabase
          .from('customer_phones')
          .insert([{
            customer_id: customer.id,
            phone: formattedPhone,
            is_primary: true,
            label: 'Main Phone'
          }])
      );

      // Wait for all contact records to be created
      await Promise.all(promises);

      setCustomers(prev => [customer, ...prev]);
      return { duplicate: false, customer };
    } catch (err) {
      console.error('Error adding customer:', err);
      throw err;
    }
  };

  return { customers, loading, error, addCustomer, duplicateCustomer, setDuplicateCustomer };
}

export function useCustomerContacts(customerId: string) {
  const [contacts, setContacts] = useState<CustomerContact[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchContacts = async () => {
    if (!customerId) {
      setContacts([]);
      setLoading(false);
      return;
    }

    try {
      const [addressesRes, emailsRes, phonesRes] = await Promise.all([
        supabase
          .from('customer_addresses')
          .select('*')
          .eq('customer_id', customerId)
          .order('is_primary', { ascending: false }),
        supabase
          .from('customer_emails')
          .select('*')
          .eq('customer_id', customerId)
          .order('is_primary', { ascending: false }),
        supabase
          .from('customer_phones')
          .select('*')
          .eq('customer_id', customerId)
          .order('is_primary', { ascending: false })
      ]);

      if (addressesRes.error) throw addressesRes.error;
      if (emailsRes.error) throw emailsRes.error;
      if (phonesRes.error) throw phonesRes.error;

      const contacts: CustomerContact[] = [
        ...(addressesRes.data || []).map(addr => ({
          id: addr.id,
          type: 'address',
          value: `${addr.address}, ${addr.city}, ${addr.state} ${addr.zip_code}`,
          isPrimary: addr.is_primary,
          label: addr.label,
          raw: addr
        })),
        ...(emailsRes.data || []).map(email => ({
          id: email.id,
          type: 'email',
          value: email.email,
          isPrimary: email.is_primary,
          label: email.label,
          raw: email
        })),
        ...(phonesRes.data || []).map(phone => ({
          id: phone.id,
          type: 'phone',
          value: phone.phone,
          isPrimary: phone.is_primary,
          label: phone.label,
          raw: phone
        }))
      ];

      setContacts(contacts);
      setError(null);
    } catch (err) {
      console.error('Error fetching contacts:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch contacts');
      setContacts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContacts();
  }, [customerId]);

  const addContact = async (type: 'address' | 'email' | 'phone', data: any) => {
    try {
      const table = `customer_${type}s`;
      
      const insertData = {
        customer_id: customerId,
        is_primary: data.is_primary,
        label: data.label,
        ...(type === 'address' ? {
          address: data.address,
          city: data.city,
          state: data.state,
          zip_code: data.zip_code
        } : type === 'email' ? {
          email: data.email
        } : {
          phone: data.phone
        })
      };

      const { data: newContact, error } = await supabase
        .from(table)
        .insert([insertData])
        .select()
        .single();

      if (error) throw error;

      const contactValue = type === 'address'
        ? `${newContact.address}, ${newContact.city}, ${newContact.state} ${newContact.zip_code}`
        : type === 'email'
          ? newContact.email
          : newContact.phone;

      const contact: CustomerContact = {
        id: newContact.id,
        type,
        value: contactValue,
        isPrimary: newContact.is_primary,
        label: newContact.label,
        raw: newContact
      };

      setContacts(prev => [...prev, contact]);
      return contact;
    } catch (err) {
      console.error('Error adding contact:', err);
      throw err;
    }
  };

  const updateContact = async (contact: CustomerContact) => {
    try {
      const table = `customer_${contact.type}s`;
      
      const { error } = await supabase
        .from(table)
        .update(contact.raw)
        .eq('id', contact.id);

      if (error) throw error;

      // Refresh contacts to ensure consistent state
      await fetchContacts();
    } catch (err) {
      console.error('Error updating contact:', err);
      throw err;
    }
  };

  const deleteContact = async (contact: CustomerContact) => {
    try {
      const table = `customer_${contact.type}s`;
      
      const { error } = await supabase
        .from(table)
        .delete()
        .eq('id', contact.id);

      if (error) throw error;

      setContacts(prev => prev.filter(c => c.id !== contact.id));
    } catch (err) {
      console.error('Error deleting contact:', err);
      throw err;
    }
  };

  return { contacts, loading, error, addContact, updateContact, deleteContact };
}